import * as PopoverPrimitive from '@radix-ui/react-popover';
import { useRef } from 'react';
import { cn } from '@/shared/lib/utils';
import { PopoverContent } from '@/shared/ui/Popover/components/PopoverContent';
import { Input } from '../../input/Input';
import type { AutocompleteOption } from '../helpers/types';
import { useAutocomplete } from '../hooks/useAutocomplete';

type AutocompleteProps = {
  /** 現在の入力値 */
  value?: string;
  /** 値変更時のコールバック */
  onValueChange?: (value: string) => void;
  /** フォーカス離脱時のコールバック */
  onBlur?: () => void;
  /** サジェスト候補 */
  options: AutocompleteOption[];
  /** プレースホルダー */
  placeholder?: string;
  /** 無効化フラグ */
  disabled?: boolean;
  /** クリアボタンを表示するか */
  showAllClear?: boolean;
  /** エラー状態 */
  hasError?: boolean;
  /** フィールドの ID */
  id?: string;
  /** カスタムクラス名 */
  className?: string;
  /** フォーカス時に候補を表示するか（デフォルト: false） */
  showOnFocus?: boolean;
};

/**
 * Autocomplete コンポーネント
 *
 * 自由テキスト入力 + サジェスト候補表示を提供する。
 * 既存の Input コンポーネントと Radix Popover を組み合わせて実装。
 */
export const Autocomplete = ({
  value,
  onValueChange,
  onBlur,
  options,
  placeholder = '入力してください',
  disabled = false,
  showAllClear = false,
  hasError = false,
  id,
  className,
  showOnFocus = false,
}: AutocompleteProps) => {
  const anchorRef = useRef<HTMLInputElement>(null);
  const listboxId = id ? `${id}-listbox` : undefined;

  const {
    open,
    inputValue,
    filteredOptions,
    activeIndex,
    handleInputChange,
    handleSelectOption,
    handleOpenChange,
    handleFocus,
    handleKeyDown,
  } = useAutocomplete({ value, onValueChange, onBlur, options, showOnFocus });

  const activeOptionId = activeIndex >= 0 && id ? `${id}-option-${activeIndex}` : undefined;

  return (
    <PopoverPrimitive.Root open={open && !disabled} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Anchor asChild>
        <Input
          ref={anchorRef}
          id={id}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          showAllClear={showAllClear}
          autoComplete={false}
          aria-invalid={hasError}
          aria-describedby={hasError && id ? `${id}-error` : undefined}
          role="combobox"
          aria-expanded={open && !disabled}
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={activeOptionId}
          className={className}
        />
      </PopoverPrimitive.Anchor>
      {filteredOptions.length > 0 && (
        <PopoverContent
          className="w-(--radix-popover-trigger-width) p-1"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => {
            // Input からフォーカスを奪わない
            e.preventDefault();
          }}
          onCloseAutoFocus={(e) => {
            // 閉じた後に Input にフォーカスを戻さない（既に Input にある）
            e.preventDefault();
          }}
          onInteractOutside={(e) => {
            // combobox パターン: 自身の Input（Anchor）上のクリックでは Popover を閉じない
            const target = e.target as Node;
            if (anchorRef.current?.contains(target)) {
              e.preventDefault();
            }
          }}
        >
          <div role="listbox" id={listboxId} className="max-h-60 overflow-y-auto">
            {filteredOptions.map((option, index) => (
              <div
                key={option.value}
                role="option"
                id={id ? `${id}-option-${index}` : undefined}
                aria-selected={index === activeIndex}
                tabIndex={-1}
                className={cn(
                  'cursor-pointer rounded-sm px-2 py-1.5 text-sm outline-none',
                  'hover:bg-stone-50',
                  index === activeIndex && 'bg-surface-accent',
                )}
                onMouseDown={(e) => {
                  // フォーカス移動を防止
                  e.preventDefault();
                }}
                onClick={() => handleSelectOption(option)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleSelectOption(option);
                  }
                }}
              >
                {option.filterLabel ?? option.fieldLabel}
              </div>
            ))}
          </div>
        </PopoverContent>
      )}
    </PopoverPrimitive.Root>
  );
};

Autocomplete.displayName = 'Autocomplete';
